import { batch } from 'react-redux';
import { AbortedError } from 'src/common/api/extended-emerald-error';
import { delay } from 'src/common/helpers/delay-helper';
import {
  beginPaymentSession,
  cancelPinpadOperation,
  endPaymentSession,
  inPaymentSession,
} from 'src/devices/payment/pinpad-operations';
import { validateCard } from 'src/home-page/business-logic/validate-card';
import { EntryMethod } from '../../common/enumerations/entry-method';
import { RefundUsage } from '../../common/interfaces/dtos/IPreTenderAdd';
import { ITender } from '../../common/interfaces/dtos/ITender';
import { store } from '../../common/redux/configure-store';
import { TenderMenuActions } from '../../common/redux/reducers/tender-menu-reducer';
import { TenderingActions } from '../../common/redux/reducers/tendering-reducer';
import {
  getCardData,
  hasCardData,
  resetCardData,
  resetForManualEntry,
  waitForCardDetails,
} from '../../devices/payment/get-card-details';
import { showEftTenderingView } from '../components/process-tender/tender-processing-view';
import { getEPSData } from './get-eps-data';
import { AddTenderProps, findMatchingTenderInStore } from './handle-tender-payment';
import { processAsyncEFTTender } from './process-async-eft-tender';

// Exit button in EFT tendering disabled when got cardDetails
// Click exit before got cardDetail will reject promise and stop payment flow
export const showTenderingAndGetCardDetails = (props: AddTenderProps): Promise<void> => {
  const prepareCardDetails =
    props.usage === RefundUsage.Refund ? prepareRefundCardDetails : preparePaymentCardDetails;

  return new Promise<void>((resolve, reject) => {
    showEftTenderingView(() => reject(new AbortedError()));
    prepareCardDetails(props)
      .catch(reject)
      .then(resolve);
  });
};

const PAYMENT_SESSION_BUFFER = 1000;

const preparePaymentCardDetails = async (props: AddTenderProps): Promise<void> => {
  const { tender, amountDue, amount } = props;
  let entryMethod: EntryMethod | undefined = undefined;
  const manualCardEntryPrompt = 'ManualCardEntryPrompt';
  const callGetCardDetailsByTenderType = store.getState().touchPoint.touchPointConfiguration
    .callGetCardDetailsByTenderType;

  if (!hasCardData()) {
    if (tender.manualEntry || tender.isAccountNumberRequired) {
      resetForManualEntry();
      await cancelPinpadOperation();
      await endPaymentSession();
      // Sometimes when showing forms, the PIN Pad is busy processing a request
      // and subsequent ones get automatically cancelled. Opening an issue to get this addressed
      // since they should just queue up (??), doing a short delay for now
      await delay(PAYMENT_SESSION_BUFFER);
      await beginPaymentSession();

      entryMethod = EntryMethod.Manual;
      batch(() => {
        resetCardData();
        store.dispatch(TenderingActions.setPaymentInfoText(manualCardEntryPrompt));
      });
    }
    if (!callGetCardDetailsByTenderType || !isTenderAuthorizeWithoutIdentification(tender)) {
      await waitForCardDetails(amount, amountDue, entryMethod);
    }
  }
};

const prepareRefundCardDetails = async (props: AddTenderProps): Promise<void> => {
  const { amountDue } = props;

  store.dispatch(TenderMenuActions.setSelectedTender(props.tender));
  store.dispatch(TenderMenuActions.setSelectedTenderGroup('Credit/Debit'));

  if (!hasCardData()) {
    if (!inPaymentSession()) {
      await beginPaymentSession();
    }
    await waitForCardDetails(amountDue, amountDue);
  }
};

export const addEFTTender = async (props: AddTenderProps): Promise<void> => {
  const { tender, amountDue, usage, cashbackAmount } = props;
  let { amount } = props;

  const epsData = getEPSData(tender.id);
  const matchingTender = findMatchingTenderInStore(epsData);
  if (matchingTender?.hasAllowedAmountRestriction) {
    amount = matchingTender.maxAllowedAmount || amount;
  }
  return processAsyncEFTTender(epsData, amountDue, amount, usage, cashbackAmount);
};

export const addRefundEFTTender = async (props: AddTenderProps): Promise<void> => {
  const { amountDue, usage } = props;

  const epsData = getCardData();
  const isValidCard = await validateCard(epsData);
  if (!isValidCard) {
    resetCardData();
  } else {
    await processAsyncEFTTender(epsData, amountDue, undefined, usage);
    resetCardData();
  }
};

const isTenderAuthorizeWithoutIdentification = (tender: ITender): boolean => {
  const tenders: ITender[] = store.getState().tenderMenu.sellableTenders.rawTenders;
  return tenders.some(f => f.isAuthorizeWithoutIdentification && f.id == tender.id);
};
